import { useState, useEffect, lazy, Suspense } from 'react';
import Loader from '../shared/Loader';
import AdminErrorBoundary from './AdminErrorBoundary';
import '../../styles/Dashboard.css';

// Lazy-loaded tab components (Phase 7)
const ProductsTab = lazy(() => import('./ProductsTab'));
const Orders = lazy(() => import('./Orders'));
const CategoryManager = lazy(() => import('./CategoryManager'));
const HomeContentManager = lazy(() => import('./HomeContentManager'));
const StockManager = lazy(() => import('./StockManager'));
const DiscountsManager = lazy(() => import('./DiscountsManager'));
const EnhancedMediaLibrary = lazy(() => import('./EnhancedMediaLibrary'));
const StudioIntegration = lazy(() => import('./StudioIntegration'));
const DescriptionManager = lazy(() => import('./DescriptionManager'));
const BulkVariantManager = lazy(() => import('./BulkVariantManager'));
const BulkImport = lazy(() => import('./BulkImport'));
const ChatStats = lazy(() => import('./ChatStats'));
const SizeGuidesTab = lazy(() => import('./SizeGuidesTab'));
const StoreLocationsTab = lazy(() => import('./StoreLocationsTab'));
const HelpSupport = lazy(() => import('./HelpSupport'));
const EmailManager = lazy(() => import('./EmailManager'));

const TABS = [
  { id: 'orders', label: 'Órdenes', icon: 'fa-shopping-cart' },
  { id: 'products', label: 'Productos', icon: 'fa-box' },
  { id: 'home-content', label: 'Configuración', icon: 'fa-cog' },
  { id: 'categories', label: 'Categorías', icon: 'fa-tags' },
  { id: 'stock-manager', label: 'Gestor de Stock', icon: 'fa-boxes-stacked' },
  { id: 'discounts', label: 'Descuentos', icon: 'fa-percentage' },
  { id: 'media', label: 'Biblioteca de Medios', icon: 'fa-images' },
  { id: 'studio', label: 'Studio Moreira', icon: 'fa-palette' },
  { id: 'description-manager', label: 'Gestor de Descripciones', icon: 'fa-file-alt' },
  { id: 'variants', label: 'Gestor de Variantes', icon: 'fa-th' },
  { id: 'bulk-import', label: 'Importación Masiva', icon: 'fa-file-upload' },
  { id: 'chat-stats', label: 'Chat Inteligente', icon: 'fa-comments' },
  { id: 'size-guides', label: 'Tablas de Medidas', icon: 'fa-ruler-combined' },
  { id: 'store-locations', label: 'Locales', icon: 'fa-map-marker-alt' },
  { id: 'email', label: 'Mailing', icon: 'fa-envelope' },
  { id: 'help', label: 'Ayuda y Tutoriales', icon: 'fa-question-circle' },
];

const renderTab = (activeTab) => {
  switch (activeTab) {
    case 'orders':
      return <Orders />;
    case 'products':
      return <ProductsTab />;
    case 'home-content':
      return <HomeContentManager />;
    case 'categories':
      return <CategoryManager />;
    case 'stock-manager':
      return <StockManager />;
    case 'discounts':
    case 'bulk-discount':
    case 'price-manager':
      return <DiscountsManager />;
    case 'media':
      return <EnhancedMediaLibrary mode="dashboard" />;
    case 'studio':
      return <StudioIntegration />;
    case 'description-manager':
      return <DescriptionManager />;
    case 'variants':
      return <BulkVariantManager />;
    case 'bulk-import':
      return <BulkImport />;
    case 'chat-stats':
      return <ChatStats />;
    case 'size-guides':
      return <SizeGuidesTab />;
    case 'store-locations':
      return <StoreLocationsTab />;
    case 'email':
      return <EmailManager />;
    case 'help':
      return <HelpSupport />;
    default:
      return <Orders />;
  }
};

const Dashboard = () => {
  const [activeTab, setActiveTab] = useState('orders');

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [activeTab]);

  return (
    <div className="dashboard-container">
      {/* Sidebar */}
      <div className="dashboard-sidebar">
        <div className="dashboard-sidebar-header">
          <h2>Dashboard</h2>
          <p>Panel de Control</p>
        </div>
        <nav className="dashboard-sidebar-menu">
          {TABS.map(({ id, label, icon }) => (
            <div
              key={id}
              className={`dashboard-sidebar-item ${activeTab === id ? 'active' : ''}`}
              onClick={() => setActiveTab(id)}
            >
              <i className={`fas ${icon}`}></i>
              <span>{label}</span>
            </div>
          ))}
        </nav>
      </div>

      {/* Main Content */}
      <div className="dashboard-main">
        <AdminErrorBoundary key={activeTab}>
          <Suspense fallback={<Loader />}>
            {renderTab(activeTab)}
          </Suspense>
        </AdminErrorBoundary>
      </div>
    </div>
  );
};

export default Dashboard;
